import { CANVAS_SAVE_DEBOUNCE_MS } from './constants.js';
import { animateMapChanges } from './map_animation.js';

// Blend the completed fog masks, never individual paint/erase strokes. Unchanged
// opaque coverage stays opaque even where several strokes overlap.
const FRINGE = 12;

export class FogPresentation {
  constructor(layer, { fringe = false, animate = true } = {}) {
    this.layer = layer;
    this.animate = animate;
    this.canvas = layer.getCanvas()._canvas;
    this.visibility = this.canvas.style.visibility;
    if (fringe) {
      this.canvas.classList.add('game-map-fog');
      this.fringe = document.createElement('canvas');
      this.fringe.className = 'game-map-fog-fringe';
      this.fringe.setAttribute('aria-hidden', 'true');
      Object.assign(this.fringe.style, {
        position: 'absolute',
        left: `-${FRINGE}px`,
        top: `-${FRINGE}px`,
        pointerEvents: 'none'
      });
      this.canvas.after(this.fringe);
    }
    this.onDraw = () => {
      // Screen-space snapshots cannot follow a new pan, zoom or viewport size.
      // Settle them before drawing the new view rather than leaving stale fog.
      if (this.from && this.view !== this.viewSignature()) this.finish();
      this.renderFringe();
    };
    layer.on('draw.fogPresentation', this.onDraw);
  }

  renderFringe() {
    if (!this.fringe) return;
    const source = this.transition || this.canvas;
    const width = this.layer.width();
    const height = this.layer.height();
    const ratio = this.layer.getCanvas().getPixelRatio();
    if (this.fringe.width !== width + FRINGE * 2) this.fringe.width = width + FRINGE * 2;
    if (this.fringe.height !== height + FRINGE * 2) this.fringe.height = height + FRINGE * 2;
    this.fringe.style.width = `${this.fringe.width}px`;
    this.fringe.style.height = `${this.fringe.height}px`;
    const context = this.fringe.getContext('2d');
    context.clearRect(0, 0, this.fringe.width, this.fringe.height);
    // Stable irregular scallops: no random flicker on pan, resize or broadcasts.
    for (let side = 0; side < 4; side += 1) {
      const horizontal = side < 2;
      const length = horizontal ? width : height;
      for (let position = 12; position < length - 12; position += 16) {
        const radius = 7 + 3 * Math.sin(position * 0.17 + side * 2);
        const x = horizontal ? position : side === 2 ? 0 : width;
        const y = horizontal ? (side === 0 ? 0 : height) : position;
        context.save();
        context.beginPath();
        context.ellipse(x + FRINGE, y + FRINGE, horizontal ? 12 : radius, horizontal ? radius : 12, 0, 0, Math.PI * 2);
        context.clip();
        // Extend only the fog actually touching this edge. Erased edges stay clear.
        context.drawImage(
          source,
          (horizontal ? position - 12 : side === 2 ? 0 : width - 1) * ratio,
          (horizontal ? (side === 0 ? 0 : height - 1) : position - 12) * ratio,
          (horizontal ? 24 : 1) * ratio,
          (horizontal ? 1 : 24) * ratio,
          x + FRINGE - (horizontal ? 12 : radius),
          y + FRINGE - (horizontal ? radius : 12),
          horizontal ? 24 : radius * 2,
          horizontal ? radius * 2 : 24
        );
        context.restore();
      }
    }
    context.clearRect(FRINGE, FRINGE, width, height);
  }

  beforeUpdate() {
    if (!this.animate || !animateMapChanges()) return this.finish();
    this.layer.draw();
    const from = document.createElement('canvas');
    from.width = this.canvas.width;
    from.height = this.canvas.height;
    // Retarget from the currently visible blend if another update arrives early.
    from.getContext('2d').drawImage(this.transition || this.canvas, 0, 0);
    this.finish();
    this.from = from;
    this.view = this.viewSignature();
  }

  afterUpdate() {
    if (!this.from) return;
    this.layer.draw();
    if (!this.from) return; // A simultaneous viewport change settled the update.
    this.transition = document.createElement('canvas');
    this.transition.width = this.canvas.width;
    this.transition.height = this.canvas.height;
    this.transition.className = this.fringe ? 'game-map-fog game-map-fog-transition' : 'game-map-fog-transition';
    this.transition.setAttribute('aria-hidden', 'true');
    this.transition.style.cssText = this.canvas.style.cssText;
    this.transition.style.pointerEvents = 'none';
    this.canvas.after(this.transition);
    this.canvas.style.visibility = 'hidden';
    this.startedAt = performance.now();
    this.renderTransition(this.startedAt);
  }

  renderTransition(now) {
    this.frame = null;
    const progress = Math.max(0, Math.min(1, (now - this.startedAt) / CANVAS_SAVE_DEBOUNCE_MS));
    if (progress === 1 || !animateMapChanges()) return this.finish();
    const context = this.transition.getContext('2d');
    // Add weighted, premultiplied pixels. Ordinary source-over crossfading would
    // make unchanged fog translucent halfway through the transition.
    context.globalCompositeOperation = 'copy';
    context.globalAlpha = 1 - progress;
    context.drawImage(this.from, 0, 0);
    context.globalCompositeOperation = 'lighter';
    context.globalAlpha = progress;
    context.drawImage(this.canvas, 0, 0);
    this.syncOpacity();
    this.renderFringe();
    this.frame = requestAnimationFrame((time) => this.renderTransition(time));
  }

  syncOpacity() {
    if (this.transition) this.transition.style.opacity = this.canvas.style.opacity;
  }

  viewSignature() {
    return [this.canvas.width, this.canvas.height, ...this.layer.getAbsoluteTransform().getMatrix()].join(',');
  }

  finish() {
    if (this.frame != null) cancelAnimationFrame(this.frame);
    this.frame = null;
    this.transition?.remove();
    this.transition = null;
    this.from = null;
    this.canvas.style.visibility = this.visibility;
    this.renderFringe();
  }

  destroy() {
    this.finish();
    this.layer.off('draw.fogPresentation', this.onDraw);
    this.fringe?.remove();
  }
}
